<template>
    <div class="ma-4">
        <v-snackbar
            v-model="msg"
            top
            :color="snackbarColor"
            :timeout="3000"
            elevation="4"
            class="rounded-lg"
        >
            <div class="d-flex align-center">
                <v-icon class="mr-2">{{
                    snackbarColor === "success" ? "mdi-check-circle" : "mdi-alert"
                }}</v-icon>
                {{ text }}
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn icon small v-bind="attrs" @click="msg = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>

        <v-card class="rounded-lg">
            <v-app-bar flat dense color="grey lighten-4" class="rounded-t-lg px-3">
                <v-btn icon small class="mr-2 grey lighten-3" @click="$router.go(-1)">
                    <v-icon>navigate_before</v-icon>
                </v-btn>

                <span class="text-h6 font-weight-medium grey--text text--darken-3">
                    Vehicle Maintenance Report
                </span>

                <v-spacer></v-spacer>

                <report-list-actions
                    :reportOptions="reportOptions"
                    :downloadFilename="reportListFilename"
                ></report-list-actions>
            </v-app-bar>

            <v-divider></v-divider>

            <v-card-text class="pa-6 grey lighten-5">
                <v-row dense>
                    <v-col cols="12" md="3">
                        <v-menu
                            ref="calendarMenu"
                            v-model="calendarMenu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.startDate"
                                    label="Start Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.startDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendarMenu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendarMenu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-menu
                            ref="calendar2Menu"
                            v-model="calendar2Menu"
                            :close-on-content-click="false"
                            :return-value.sync="date"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    v-model="reportFilters.endDate"
                                    label="End Date"
                                    prepend-icon="mdi-calendar"
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    :rules='requiredField'
                                    clearable
                                    outlined
                                    dense
                                    hide-details
                                    class="rounded-lg"
                                    background-color="white"
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="reportFilters.endDate"
                                no-title
                                scrollable
                                :rules='requiredField'
                                class="rounded-lg"
                            >
                                <v-spacer></v-spacer>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="calendar2Menu = false"
                                >
                                    Cancel
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="$refs.calendar2Menu.save(date)"
                                >
                                    OK
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-select 
                            label="Select Vehicle"
                            :items="vehicleList"
                            v-model="reportFilters.vehicle"
                            clearable
                            :item-text="vehicleText"
                            :item-value="vehicleText"
                            outlined
                            dense
                            hide-details
                            class="rounded-lg"
                            background-color="white"
                        ></v-select>
                    </v-col>

                    <v-col cols="12" md="3">
                        <v-btn
                            color="primary"
                            block
                            height="40"
                            class="rounded-lg"
                            :loading="isLoading"
                            elevation="1"
                            @click="vehicleQuery()"
                        >
                            <v-icon left>mdi-magnify</v-icon>
                            Search
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-text>

            <!-- Data Table -->
            <v-card-text class="pa-6">
                <v-data-table
                    :headers="headers"
                    :items="vehicles"
                    sort-by="Date"
                    sort-asc
                    class="elevation-1 rounded-lg"
                    :loading="isLoading"
                    loading-text="Loading Vehicle Maintenance Logs"
                    :items-per-page="100"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 20, 50, 100],
                        showFirstLastPage: true,
                        'items-per-page-text': 'Rows per page:',
                        class: 'rounded-lg',
                    }"
                >
                    <template v-slot:item.actions="{ item }">
                        <v-btn
                            x-small
                            color="primary"
                            @click="viewMaintenance(item)"
                            class="px-3"
                        >
                            <v-icon left x-small>mdi-eye</v-icon>
                            View
                        </v-btn>
                    </template>
                </v-data-table>

                <v-dialog v-model="dialog" max-width="80%" class="rounded-lg">
                    <v-card class="rounded-lg">
                        <v-card-title class="primary white--text">
                            <span class="text-h6">{{ titleForm }}</span>
                        </v-card-title>
                        <v-card-text class="pt-6">
                            <v-container>
                                <v-row>
                                    <v-col cols="12" sm="12" md="4">
                                        <v-select
                                            label="Vehicle"
                                            v-model="newVehicle"
                                            :items="vehicleList"
                                            prepend-icon="local_taxi"
                                            :item-text="vehicleText"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        ></v-select>
                                    </v-col>
                                    <v-col cols="12" sm="12" md="4">
                                        <v-menu
                                            ref="menu"
                                            v-model="menu"
                                            :close-on-content-click="false"
                                            :return-value.sync="date"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="auto"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="newDate"
                                                    label="Date"
                                                    prepend-icon="mdi-calendar"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :readonly="readonly"
                                                    outlined
                                                    dense
                                                    class="rounded-lg"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="newDate"
                                                no-title
                                                scrollable
                                                class="rounded-lg"
                                            >
                                                <v-spacer></v-spacer>
                                                <v-btn
                                                    text
                                                    color="primary"
                                                    @click="menu = false"
                                                >
                                                    Cancel
                                                </v-btn>
                                                <v-btn
                                                    text
                                                    color="primary"
                                                    @click="$refs.menu.save(date)"
                                                >
                                                    OK
                                                </v-btn>
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                    <v-col cols="12" sm="12" md="4">
                                        <v-text-field
                                            label="Service"
                                            v-model="newService"
                                            outlined
                                            dense
                                            class="rounded-lg"
                                        ></v-text-field>
                                    </v-col>
                                    <v-col cols="12" sm="12">
                                        <v-textarea
                                            label="Notes"
                                            v-model="newNotes"
                                            outlined
                                            auto-grow
                                            rows="4"
                                            class="rounded-lg"
                                        ></v-textarea>
                                    </v-col>
                                    <v-col cols="12" sm="12" md="12">
                                        <v-menu
                                            ref="menu2"
                                            v-model="menu2"
                                            :close-on-content-click="false"
                                            :return-value.sync="date"
                                            transition="scale-transition"
                                            offset-y
                                            min-width="auto"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-text-field
                                                    v-model="newNextAppointment"
                                                    label="Next Appointment"
                                                    prepend-icon="mdi-calendar"
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    :readonly="readonly"
                                                    outlined
                                                    dense
                                                    class="rounded-lg"
                                                ></v-text-field>
                                            </template>
                                            <v-date-picker
                                                v-model="newNextAppointment"
                                                no-title
                                                scrollable
                                                class="rounded-lg"
                                            >
                                                <v-spacer></v-spacer>
                                                <v-btn
                                                    text
                                                    color="primary"
                                                    @click="menu2 = false"
                                                >
                                                    Cancel
                                                </v-btn>
                                                <v-btn
                                                    text
                                                    color="primary"
                                                    @click="$refs.menu2.save(date)"
                                                >
                                                    OK
                                                </v-btn>
                                            </v-date-picker>
                                        </v-menu>
                                    </v-col>
                                </v-row>
                                <v-carousel v-if="newImage.length > 0">
                                    <template
                                        v-for="(item, index) in imageDetails"
                                    >
                                        <v-carousel-item :key="index">
                                            <v-row
                                                class="flex-nowrap"
                                                style="height: 100%"
                                            >
                                                <v-img :src="item.AWSLink">
                                                    <v-row
                                                        class="fill-height"
                                                        align="center"
                                                        justify="center"
                                                    >
                                                        <div
                                                            style="
                                                                color: #000;
                                                                padding-top: 30%;
                                                            "
                                                        >
                                                            {{ item.Name }}
                                                        </div>
                                                    </v-row>
                                                </v-img>
                                            </v-row>
                                        </v-carousel-item>
                                    </template>
                                </v-carousel>
                            </v-container>
                        </v-card-text>

                        <v-card-actions class="pa-4">
                            <v-spacer></v-spacer>
                            <v-btn
                                color="primary"
                                @click="updateMaintenance()"
                                v-if="editMaintenance"
                            >
                                Update
                            </v-btn>
                            <v-btn
                                color="grey"
                                text
                                @click="close"
                            >
                                Close
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-card-text>
        </v-card>
    </div>
</template>
<script>
import axios from 'axios'
import ReportListActions  from '@/components/Report/ReportListActions.vue';
import ReportFilters from '@/mixins/reportFilters.js';

const url = require("../../client_config/config").url;

  export default {
    mixins: [
        ReportFilters,
    ],
    components: {
      ReportListActions
    },
    data: () => ({
    headers: [
        {
        text: 'Date',
        align: 'start',
        sortable: true,
        value: 'Date',
        },
        { text: "Vehicle", value: "Vehicle" },
        { text: "Service", value: "Service" },
        { text: "Next Service", value: "NextAppointment" },
        { text: "Actions", value: "actions", sortable: false },
    ],
    date: (new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10),
    vehicleList: [],
    vehicles: [],
    dialog: false,
    calendarMenu: false,
    calendar2Menu: false,
    menu: false,
    menu2: false,
    role: localStorage.getItem('userRole'),
    state: localStorage.getItem('userState'),
    requiredField: [
        v => !!v || 'This is required',
    ],
    titleForm: "New Maintenance Log",
    maintenanceID: "",
    newVehicle: "",
    newOtherVehicle: "",
    newDate: "",
    newService: "",
    newNotes: "",
    newDocumentation: "",
    newNextAppointment: "",
    newCurrentMileage: "",
    isLoading: true,
    StaffID: localStorage.getItem('staffId'),
    newImage: [],
    imageDetails: [],
    imageLink: "",
    newVehicles: [],
    removedDocuments: [],
    uploadImg: [],
    msg: false,
    snackbarColor: "success",
    text: '',
    reportListFilename: 'Vehicle Maintenance Report',
    options: {},
    editMaintenance: false,
    reportFilters:  {
        startDate: '',
        endDate: '',
        vehicle: {}
    },
    reportFilterName: 'ReportFilters_VehicleMaintenanceReport',
  }),

    computed: {
      vehicleText() {
      return function (item) {
        // Concatenate the desired fields (e.g., Make, Model, Year)
        return `${item.Year} ${item.Make} ${item.Model}`;
      };
    },
      queryParams() {
          let {page, sortBy, sortDesc, itemsPerPage} = this.options;      
          const sortField = sortBy ? sortBy[0] : 'Date';
          const sortDirection = sortDesc ? sortDesc[0] : 'desc';
          const companyIds = localStorage.getItem('userCompanies');

          return {            
            startDate: this.reportFilters.startDate,
            endDate: this.reportFilters.endDate,
            vehicle: this.reportFilters.vehicle,
            Company: JSON.parse(companyIds),
            sortField: sortField,
            sortDirection: sortDirection,
            page: page,
            limit: itemsPerPage,
          };
      },
  
      reportOptions() {
          return { 
              query: this.queryParams,
              endpoints: `vehicleMaintenance/print-report`,
              dynamic: true
          }
      }

    },

    created() {
      this.getVehicles();
      this.reportFilters = this.getReportFilters(this.reportFilterName) ?? this.reportFilters;
    },        

    watch: {
      reportFilters: {
        handler(newVal) {
        this.setReportFilters(this.reportFilterName, newVal);
        },
        deep: true
      }
    },

    methods: {
      initialize() {
        this.vehicles = [];
      },

      getVehicles() {
        this.isLoading = true;
        axios.get(`${url}/vehicles/active/${this.state}`).then(res => {
            this.vehicleList = res.data.vehicles.map(vehicle => `${vehicle.Year + ' ' + vehicle.Make + ' ' + vehicle.Model}` )
                    .sort();

        }).finally(() => this.isLoading = false);
      },
      close() {
        this.dialog = false;

      },
      
      async vehicleQuery() {
        this.vehicles = [];
        this.isLoading = true;
        this.errorMessage = ''; // Add a field to display error message

        try {
          const response = await axios.get(`${url}/vehicleMaintenance/maintenance/report/query`, {
            params: this.queryParams,
          });

          this.vehicles = response.data.vehicles;
          this.isLoading = false;
        } catch (error) {
          this.errorMessage = 'An error occurred while fetching data.'; // Set an error message
          this.isLoading = false; // Make sure to disable loading state
        }
      },

      getStaffID(item) {
      this.staff = item.EmployeeID;
      window.localStorage.setItem('staffId', this.staff)
      console.log(this.staff + " is the staff")
    },

    viewMaintenance(item) {
      console.log(item);
        this.titleForm= "Viewing " + item.Service;
        this.dialog = true;
        this.editMaintenance = true;
        this.newMaintenance = false;
        this.newVehicle = item.Vehicle;
        this.newOtherVehicle = item.OtherVehicle;
        this.newDate = item.Date;
        this.newService = item.Service;
        this.newNotes = item.Notes;
        this.newDocumentation = item.Documentation;
        this.newNextAppointment = item.NextAppointment;
        this.newCurrentMileage = item.CurrentMileage;
        this.maintenanceID = item._id;

        if (item.documents.length > 0) {
          const tempData = [];
          for (let i = 0; i < item.documents.length; i++) {
            let fileExt = item.documents[i].AWSLink.split(".").pop();
            tempData.push({
              name: item.documents[i].Name,
              ext: fileExt,
              id: item.documents[i]._id,
              awsLink: item.documents[i].AWSLink,
            });
          }
          this.newImage = tempData;
          this.newImageLink = this.newImage[0].awsLink;
        }

        this.imageDetails = item.documents;
      },

      updateMaintenance() {
          const companyIds = localStorage.getItem('userCompanies');

          let req_body = {
          Vehicle: this.newVehicle,
          OtherVehicle: this.newOtherVehicle,
          Date: this.newDate,
          Service: this.newService,
          Documentation: this.newDocumentation,
          Notes: this.newNotes,
          NextAppointment: this.newNextAppointment,
          CurrentMileage: this.newCurrentMileage,
          Company: JSON.parse(companyIds)
        };

        axios.put(`${url}/vehicleMaintenance/${this.maintenanceID}`, req_body).then((response) => {
          if (response.status === 400) {
            response.json().then(function(data) {
              alert(data.msg);
            });
          }
          
          if (this.uploadImg !== null && this.uploadImg !== undefined) {
            this.saveImg();
            if (this.removedDocuments.length > 0) {
                this.deleteImages();
            }
          }

          this.msg =true;
          this.text ="The maintenance log has been updated successfully.";
        });
        this.close();
      },

      imgSelected(e) {
        this.uploadImg = e;
      },

      saveImg() {
        this.message = "";
        for (let i = 0; i < this.uploadImg.length; i++) {
          this.uploadFiles(this.uploadImg[i]);
        }
      },

      uploadFiles(file) {
        const formData = new FormData();
        formData.append("vehicleMaintenance-img", file);
        var ct = { headers: { "content-type": "multipart/form-data" } };
        axios
          .post(`${url}/vehicleMaintenance/img/uploader/${this.maintenanceID}`, formData, ct)
          .then((res) => {
            if (res.status == 400) {
              res.json().then(function(data) {
                this.text=data.msg;
              });
            }

            let vehicleIndex = this.vehicles.findIndex((vehicle) => vehicle._id === this.maintenanceID);
            this.vehicles[vehicleIndex].documents.push(res.data.document);
          })
          .catch((err) => {
            console.log(err);
          });
      },

      deleteChip(index) {
            this.removedDocuments.push(this.newImage[index]);
            this.newImage.splice(index, 1);
      },
      
      deleteImages() {
        let vehicleIndex = this.vehicles.findIndex((vehicle) => vehicle._id === this.maintenanceID);
        this.removedDocuments.forEach(document => {
            let documentIndex = this.vehicles[vehicleIndex].documents.findIndex((doc) => doc._id === document.id);
            axios.delete(`${url}/documents/${document.id}`)
            .then(res => {
                if (res.status === 204)  {
                    console.log(document.id + ' has been deleted.');
                }
                this.vehicles[vehicleIndex].documents.splice(documentIndex, 1);
            })
            .catch(err => {
                console.log(err);
            }); 
        });
      },

     printTable() {
        window.print()
      
    },
    }
  }
</script>
<style scoped>
.v-data-table {
  border: 1px solid #e0e0e0;
}

.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.v-data-table ::v-deep tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.v-btn {
  text-transform: none;
  letter-spacing: 0;
}

.v-text-field ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.v-select ::v-deep .v-input__prepend-inner {
  margin-top: 8px !important;
}

.rounded-lg {
  border-radius: 8px !important;
}

.rounded-t-lg {
  border-top-left-radius: 8px !important;
  border-top-right-radius: 8px !important;
}
</style>

<style>
#print {
  display: none
}

@media only screen and (min-width: 768px) {
  .mobile {
    display: none;
  }
}

@media only screen and (max-width: 600px) {
  .desktop {
    display: none;
  }

}



@media only screen and (min-width: 768px) {
    .mobile {
      display: none;
    }
  }

  @media only screen and (max-width: 600px) {
    .desktop {
      display: none;
    }

  }

  @media print { 
    #print{ 
    display: block; 
    border: none; 
    width: 1200px;
    padding-right: 300px;
    margin-left: -200px;

    }
    

    nav.v-navigation-drawer, header.v-toolbar, button.v-btn, div.v-card {
      display: none;
      border: none; 

    }

    @page { margin: 0; }
    body { margin: 1.6cm; }
  
    
  } 
</style>